/**
 * Presence primitives — the small repeated pieces from the wireframes.
 * @file src/web/shared/components/presence.tsx
 *
 * @llm-rule WHEN: Rendering a status dot, stat tile, section label, or person row
 * @llm-rule AVOID: Re-styling these inline per screen — they carry the design language
 */

import type { ReactNode } from 'react';
import { Avatar, AvatarFallback } from '@bloomneo/uikit';
import { DOT_TOKEN, ROLE_LABEL, type DotKind, type OrgRole } from '../domain';

/* -------------------------------------------------------------------------- */
/* Dot                                                                        */
/* -------------------------------------------------------------------------- */

export function Dot({ kind, size = 8 }: { kind: DotKind; size?: number }) {
  return (
    <span
      className="inline-block flex-none rounded-full"
      style={{ width: size, height: size, background: DOT_TOKEN[kind] }}
    />
  );
}

/** Maps an attendance record onto the dot the roster shows beside a name. */
export function attendanceDot(record: { kind?: string | null } | null | undefined): DotKind {
  if (!record) return 'neutral';
  if (record.kind === 'OFF_SITE') return 'offsite';
  return 'appreciation';
}

/* -------------------------------------------------------------------------- */
/* Section label                                                              */
/* -------------------------------------------------------------------------- */

export function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <div className="text-[11px] font-medium uppercase tracking-[0.12em] text-ink-ghost">
      {children}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Stat tile                                                                  */
/* -------------------------------------------------------------------------- */

export type StatTone = 'plain' | 'warn' | 'bad' | 'ok';

const STAT_TONE: Record<StatTone, string> = {
  plain: 'bg-paper-sunk ring-rule',
  warn: 'bg-[var(--state-warn-bg)] ring-[var(--state-warn-border)]',
  bad: 'bg-[var(--state-bad-bg)] ring-[var(--state-bad-border)]',
  ok: 'bg-[var(--state-ok-bg)] ring-[var(--state-ok-border)]',
};

export function StatTile({
  value,
  label,
  tone = 'plain',
}: {
  value: ReactNode;
  label: string;
  tone?: StatTone;
}) {
  return (
    <div className={`flex-1 rounded-[14px] p-3.5 ring-1 ring-inset ${STAT_TONE[tone]}`}>
      <div className="tnum text-[22px] font-semibold leading-none tracking-[-0.02em] text-ink">
        {value}
      </div>
      <div className="mt-1.5 text-[11.5px] text-ink-faint">{label}</div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Person avatar + identity                                                   */
/* -------------------------------------------------------------------------- */

export interface PersonLike {
  id: string;
  name: string | null;
  initials?: string | null;
  orgRole?: OrgRole;
  title?: string | null;
}

export function PersonAvatar({ person, size = 26 }: { person: PersonLike; size?: number }) {
  const initials =
    person.initials ||
    (person.name ?? '?')
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0])
      .join('')
      .toUpperCase();

  return (
    <Avatar style={{ width: size, height: size }} className="flex-none">
      <AvatarFallback
        className="font-semibold text-white"
        style={{
          fontSize: Math.max(9, size * 0.4),
          background: 'var(--brand-ramp)',
          letterSpacing: '0.01em',
        }}
      >
        {initials}
      </AvatarFallback>
    </Avatar>
  );
}

export function RoleBadge({ role }: { role: OrgRole }) {
  return (
    <span className="rounded-full border border-rule px-2 py-0.5 text-[10.5px] font-medium text-ink-faint">
      {ROLE_LABEL[role]}
    </span>
  );
}

/* -------------------------------------------------------------------------- */
/* Location chip — the green / red "inside HQ" strip                          */
/* -------------------------------------------------------------------------- */

export function LocationChip({
  inside,
  title,
  detail,
}: {
  inside: boolean;
  title: string;
  detail?: string;
}) {
  return (
    <div
      className="flex items-center gap-2.5 rounded-xl px-3.5 py-3"
      style={{
        background: inside ? 'var(--state-ok-bg)' : 'var(--state-bad-bg)',
        border: `1px solid ${inside ? 'var(--state-ok-border)' : 'var(--state-bad-border)'}`,
      }}
    >
      <Dot kind={inside ? 'appreciation' : 'flag'} />
      <div className="min-w-0">
        <div className="truncate text-[13px] font-medium text-ink">{title}</div>
        {detail && <div className="truncate text-[11.5px] text-ink-faint">{detail}</div>}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Card shell — the rounded, hairline-bordered surface used everywhere         */
/* -------------------------------------------------------------------------- */

export function Panel({
  children,
  className = '',
  padded = true,
}: {
  children: ReactNode;
  className?: string;
  padded?: boolean;
}) {
  return (
    <div
      className={`rounded-[18px] border border-rule bg-paper elev-1 ${
        padded ? 'p-5' : ''
      } ${className}`}
    >
      {children}
    </div>
  );
}
